/**
 * member-guards — who may be removed or re-roled, and why not.
 *
 * FEAT-087 / S-374. Two house rules from the handoff meet here:
 *
 *   - The **last-admin guard is visible**. The server already refuses to
 *     remove or demote a workspace's only admin (409, "Cannot remove the last
 *     admin from this workspace"), but the UI let you click Remove and find
 *     out afterwards. A guard you discover by tripping it is not a guard.
 *   - **Wherever a control is disabled, the UI states why in adjacent text.**
 *     The reasons were in `title` attributes, which appear only if you hover
 *     the control you were told not to use.
 *
 * Pure, so the rules are tested rather than inferred from a disabled
 * attribute, and so both the row and its explanation read from one source.
 */

export interface GuardedMember {
  clerk_user_id: string;
  role: string;
}

export type Guard =
  | { allowed: true }
  | { allowed: false; reason: string };

const ALLOWED: Guard = { allowed: true };

function adminCount(members: readonly GuardedMember[]): number {
  return members.filter((m) => m.role === "admin").length;
}

/**
 * Removing a member. Self-removal is refused first: on a lone admin
 * removing themselves both rules apply, and "ask another admin" is the more
 * useful sentence than "promote someone else" when there is no one else.
 */
export function canRemoveMember(
  member: GuardedMember,
  members: readonly GuardedMember[],
  selfClerkUserId: string | null | undefined
): Guard {
  if (selfClerkUserId && member.clerk_user_id === selfClerkUserId) {
    return {
      allowed: false,
      reason: "You can't remove yourself — ask another admin to do it.",
    };
  }
  if (member.role === "admin" && adminCount(members) <= 1) {
    return {
      allowed: false,
      reason:
        "This is the workspace's only admin. Make someone else an admin first, or the workspace would be left with no one who can manage it.",
    };
  }
  return ALLOWED;
}

/**
 * Changing a member's role. The same last-admin rule applies: demoting the
 * only admin strands the workspace exactly as removing them would.
 */
export function canChangeRole(
  member: GuardedMember,
  members: readonly GuardedMember[],
  selfClerkUserId: string | null | undefined
): Guard {
  if (selfClerkUserId && member.clerk_user_id === selfClerkUserId) {
    return {
      allowed: false,
      reason: "You can't change your own role — ask another admin.",
    };
  }
  if (member.role === "admin" && adminCount(members) <= 1) {
    return {
      allowed: false,
      reason:
        "This is the workspace's only admin. Promote someone else before changing this role.",
    };
  }
  return ALLOWED;
}

/**
 * An invitation past its expiry can only be resent, not revoked into
 * usefulness — the handoff has the expired row offer Resend instead.
 * A missing or unparseable expiry counts as not expired: refusing to show a
 * live invitation is worse than showing a stale one.
 */
export function isInvitationExpired(
  expiresAt: string | null | undefined,
  now: Date = new Date()
): boolean {
  if (!expiresAt) return false;
  const t = new Date(expiresAt).getTime();
  if (Number.isNaN(t)) return false;
  return t <= now.getTime();
}
